import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Line, ScanFields as ScanFieldsType } from '../types';
import { useExceptionItemList } from '../hooks/useExceptionItemList';
import { useRadSites } from '../hooks/useRadSites';
import { useCreateLocator } from '../hooks/useCreateLocator';
import { useIsRepairEligible } from '../hooks/useIsRepairEligible';
import PalletDropdown from './PalletDropdown';
import CageDropdown from './CageDropdown';
import ToteDropdown from './ToteDropdown';

interface NewLineFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (newLine: Partial<Line>) => void;
  scanFields: ScanFieldsType;
  isDarkTheme?: boolean;
  selectedHeaderToOrg?: string;
  selectedHeaderFromSite?: string;
  serialNumberFromLines?: string; // Add this prop to pass the correct serial number
  selectedHeaderToSite?: string; // Add toSite for createLocator
  toRadFromScanFields?: string; // Add this prop to pass To RAD value from ScanFields
  selectedHeaderFromOrg?: string; // Add fromOrg for search API
  editLineData?: Line; // Add prop for editing existing line
  userId?: string; // Add userId for create-locator API
  onLineUpdate?: (lineId: string, field: string, value: string) => void; // Add line update handler
  orderType?: string;
}

const NewLineForm: React.FC<NewLineFormProps> = ({
  isOpen,
  onClose,
  onSave,
  scanFields,
  isDarkTheme = false,
  selectedHeaderToOrg,
  selectedHeaderFromSite,
  serialNumberFromLines,
  selectedHeaderToSite,
  toRadFromScanFields,
  selectedHeaderFromOrg,
  editLineData,
  userId,
  onLineUpdate,
  orderType
}) => {
  const [formData, setFormData] = useState<Partial<Line>>({
    description: '',
    serialNumber: '',
    assetNumber: '',
    validatedSerialNumber: '',
    validatedAssetNumber: '',
    itemNumber: '',
    toSubinventory: '',
    trackingNumber: '',
    internalSalesOrder: '',
    isoLineNumber: '',
    repairEligibility: 'Yes',
    status: 'New',
    toRad: '',
    toPallet: '',
    toCage: '',
    toTote: ''
  });
  
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const initializedRef = useRef(false);

  const { data: searchData, fetchExceptionItems: fetchSearchItems } = useExceptionItemList();
  const { data: radSites, fetchRadSites } = useRadSites();
  const { createLocator } = useCreateLocator();
  const { checkEligibility } = useIsRepairEligible();
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [enableItemSearch, setEnableItemSearch] = useState(false);
  const [createLocatorError, setCreateLocatorError] = useState<string | null>(null);
  const [selectedSearchIndex, setSelectedSearchIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const searchDropdownRef = useRef<HTMLDivElement>(null);

  // Initialize RAD sites data when component mounts
  useEffect(() => {
    if (isOpen && radSites.length === 0) {
      fetchRadSites();
    }
  }, [isOpen, radSites.length, fetchRadSites]);

  // Update search results when searchData changes
  useEffect(() => {
    setSearchResults(searchData);
  }, [searchData]);

  useEffect(() => {

    if (isOpen && !initializedRef.current) {
      initializedRef.current = true;

      // Use serialNumberFromLines if provided, otherwise use scanned serial
      const serialNumber = serialNumberFromLines || scanFields.scanSerialNumber;
      
      // Immediately clear form data to prevent showing previous content
      const initialFormData = editLineData ? {
        // Populate from editLineData when editing existing line
        description: editLineData.description || '',
        serialNumber: editLineData.serialNumber || '',
        assetNumber: editLineData.assetNumber || '',
        validatedSerialNumber: editLineData.validatedSerialNumber || '',
        validatedAssetNumber: editLineData.validatedAssetNumber || '',
        itemNumber: editLineData.itemNumber || '',
        validatedSerialItem: editLineData.validatedSerialItem || editLineData.itemNumber || '',
        toSubinventory: editLineData.toSubinventory || editLineData.subinventory || '',
        trackingNumber: editLineData.trackingNumber || '',
        internalSalesOrder: editLineData.internalSalesOrder || '',
        isoLineNumber: editLineData.isoLineNumber || '',
        repairEligibility: editLineData.repairEligibility || 'Yes',
        status: editLineData.status || 'New',
        toRad: toRadFromScanFields || '',
        toPallet: editLineData.pallet || '',
        toCage: editLineData.cage || '',
        toTote: editLineData.tote || ''
      } : {
        // Default values for new line
        description: '',
        serialNumber: serialNumber,
        assetNumber: '',
        validatedSerialNumber: scanFields.scanSerialNumber,
        validatedAssetNumber: '',
        itemNumber: '',
        toSubinventory: scanFields.toSubinventory,
        trackingNumber: scanFields.trackingNumber,
        internalSalesOrder: '',
        isoLineNumber: '',
        repairEligibility: 'Yes',
        status: 'New',
        toRad: toRadFromScanFields || '',
        toPallet: scanFields.pallet,
        toCage: scanFields.cage,
        toTote: scanFields.tote
      };
      setFormData(initialFormData);
      
      // Clear search field and saving state
      setSearchTerm('');
      setIsSaving(false);
      
      // Clear errors when form opens
      
      // Center the popup when it opens
      setPosition({ x: 0, y: 0 });
    } else if (!isOpen) {
      initializedRef.current = false;
    }
  }, [isOpen, serialNumberFromLines, scanFields.scanSerialNumber, scanFields.trackingNumber, scanFields.toSubinventory, scanFields.pallet, scanFields.cage, scanFields.tote, toRadFromScanFields, editLineData]);

  // Clear field errors when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isOpen && !target.closest('input') && !target.closest('select') && !target.closest('[data-item-dropdown]')) {
        setFieldErrors({});
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart.x, dragStart.y]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart, handleMouseMove]);



  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-item-dropdown]')) {
        setShowItemDropdown(false);
        setSearchTerm('');
      }
    };

    if (showItemDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showItemDropdown]);

  const handleInputChange = (field: keyof Line, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }
    // Clear create-locator error when user makes changes
    if (createLocatorError) {
      setCreateLocatorError(null);
    }
  };

  const validateField = (fieldName: string, value: string | undefined): string => {
    let mandatoryFields = ['serialNumber', 'validatedSerialNumber', 'toSubinventory', 'repairEligibility', 'itemNumber'];
    
    if (mandatoryFields.includes(fieldName) && (!value || value.trim() === '')) {
      if (fieldName === 'itemNumber') {
        return enableItemSearch ? 'Item Number is required' : 'MPN is required';
      }
      const displayName = fieldName === 'serialNumber' ? 'Serial Number' :
                         fieldName === 'validatedSerialNumber' ? 'Validated Serial Number' :
                         fieldName === 'toSubinventory' ? 'To Subinventory' :
                         fieldName === 'repairEligibility' ? 'Repair Eligibility' : fieldName;
      return `${displayName} is mandatory and cannot be null`;
    }
    return '';
  };

  // Get the organizationId to use for API calls (ToRAD logic)
  const getToOrgId = () => {

    
    if (toRadFromScanFields) {
      const toRadSite = radSites.find(site => site.organizationName === toRadFromScanFields);
 
      if (toRadSite) {
 
        return toRadSite.organizationId;
      }
    }

    return selectedHeaderToOrg;
  };

  // Check repair eligibility for selected item
  const checkRepairEligibility = useCallback(async (itemNumber: string) => {
    if (!itemNumber || !selectedHeaderFromSite) {
      return 'Yes'; // Default value
    }

    try {
      const isEligible = await checkEligibility(selectedHeaderFromSite, itemNumber);
      return isEligible ? 'Yes' : 'No';
    } catch (error) {
      console.error('Error checking repair eligibility:', error);
      return 'Yes'; // Default to Yes on error
    }
  }, [checkEligibility, selectedHeaderFromSite]);

  // Handle item selection from dropdown
  const handleItemSelection = useCallback(async (item: any) => {
    // Immediately close dropdown first
    setSearchTerm('');
    setSearchResults([]);
    setSelectedSearchIndex(-1);
    setIsSearching(false);
    
    // Update form fields (including validatedSerialItem so upsert API sends the correct item)
    handleInputChange('itemNumber', item.itemNumber);
    handleInputChange('validatedSerialItem', item.itemNumber);
    handleInputChange('description', item.description);
    handleInputChange('assetNumber', item.mfgPartNumber);
    
    // Show spinner while checking repair eligibility
    setIsCheckingEligibility(true);
    
    // Check repair eligibility in background (non-blocking)
    checkRepairEligibility(item.itemNumber).then(eligibility => {
      handleInputChange('repairEligibility', eligibility);
    }).catch(error => {
      console.error('Error checking repair eligibility:', error);
      handleInputChange('repairEligibility', 'Yes'); // Default to Yes on error
    }).finally(() => {
      setIsCheckingEligibility(false);
    });
  }, [handleInputChange, checkRepairEligibility]);

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    const errors: Record<string, string> = {};
    let mandatoryFields = ['serialNumber', 'validatedSerialNumber', 'toSubinventory', 'repairEligibility', 'itemNumber'];
    
    // Clear previous create-locator error
    setCreateLocatorError(null);
    
    // Check mandatory fields
    mandatoryFields.forEach(field => {
      const error = validateField(field, formData[field as keyof Line] as string);
      if (error) {
        errors[field] = error;
      }
    });
    
    // Check additional required fields: Pallet, Cage, Tote, Subinventory
    if (!formData.toPallet || formData.toPallet.trim() === '') {
      errors.toPallet = 'Pallet is Required';
    }
    if (!formData.toCage || formData.toCage.trim() === '') {
      errors.toCage = 'Cage is Required';
    }
    if (!formData.toTote || formData.toTote.trim() === '') {
      errors.toTote = 'Tote is Required';
    }
    if (!formData.toSubinventory || formData.toSubinventory.trim() === '') {
      errors.toSubinventory = 'Subinventory is Required';
    }
    
    setFieldErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      setIsSaving(false);
      return;
    }
    
    // Call create-locator API if all required fields are present
    let locator = '';
    if (formData.toPallet && formData.toCage && formData.toTote && formData.toSubinventory && selectedHeaderToSite) {
      try {
        const result = await createLocator({
          p_user_id: userId || '',
          p_organization_name: selectedHeaderToSite,
          p_subinventory: formData.toSubinventory,
          p_pallet: formData.toPallet,
          p_cage: formData.toCage,
          p_tote: formData.toTote
        });
        
        if (result.errorMessage) {
          setCreateLocatorError(result.errorMessage);
          setIsSaving(false);
          return;
        }
        
        if (result.locator) {
          locator = result.locator;
        }
      } catch (error: any) {
        setCreateLocatorError(error.message || 'Failed to create locator');
        setIsSaving(false);
        return;
      }
    }
    
    // Map Serial Exception fields to Lines table fields
    const mappedFormData = {
      ...formData,
      pallet: formData.toPallet || '',
      cage: formData.toCage || '',
      tote: formData.toTote || '',
      subinventory: formData.toSubinventory || '',
      toLocator: locator
    };
    
    onSave(mappedFormData);
    onClose();
  };

  if (!isOpen) return null;

  const overlayStyle = {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  };

  const modalStyle = {
    backgroundColor: isDarkTheme ? '#2a2a2a' : '#fff',
    color: isDarkTheme ? '#fff' : '#000',
    padding: '15px',
    borderRadius: '8px',
    width: '600px',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
    border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
    position: 'relative' as const
  };

  const getInputStyle = (fieldName: string) => ({
    width: '100%',
    padding: '6px',
    border: `2px solid ${fieldErrors[fieldName] ? '#dc3545' : (isDarkTheme ? '#555' : '#ccc')}`,
    borderRadius: '4px',
    backgroundColor: isDarkTheme ? '#333' : '#fff',
    color: isDarkTheme ? '#fff' : '#000',
    fontSize: '12px',
    boxSizing: 'border-box' as const
  });

  const errorStyle = {
    color: '#dc3545',
    fontSize: '12px',
    marginTop: '1px',
    height: '12px'
  };

  return (
    <div style={overlayStyle}>
      <div style={{
        ...modalStyle,
        transform: `translate(${position.x}px, ${position.y}px)`,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}>
        <div 
          onMouseDown={handleMouseDown} 
          style={{ 
            cursor: 'grab', 
            userSelect: 'none',
            backgroundColor: isDarkTheme ? '#2c3e50' : '#3498db',
            color: 'white',
            padding: '15px 20px',
            margin: '-15px -15px 10px -15px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>Serial Exception</h3>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
        <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ position: 'relative', width: '500px' }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                setSelectedSearchIndex(-1);
                
                if (value.trim() && (selectedHeaderFromOrg || selectedHeaderFromSite) && selectedHeaderToOrg) {
                  setIsSearching(true);
                  // Use fromOrg if available, fallback to fromSite
                  const fromParam = selectedHeaderFromOrg || selectedHeaderFromSite;
                  
                  // Logic to determine toOrg parameter
                  let toOrgParam = selectedHeaderToOrg;
                  if (toRadFromScanFields) {
                    // Find the organizationId for the ToRAD field value
                    const toRadSite = radSites.find(site => site.organizationName === toRadFromScanFields);
                    if (toRadSite) {
                      toOrgParam = toRadSite.organizationId;
                    }
                  }
                  
                  fetchSearchItems(
                    toOrgParam,
                    fromParam,
                    value,  // searchTerm
                    enableItemSearch  // enableItemSearch
                  ).finally(() => setIsSearching(false));
                } else {
                  setSearchResults([]);
                  setIsSearching(false);
                }
              }}
              onKeyDown={(e) => {
                if (!searchResults.length) return;
                
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setSelectedSearchIndex(prev => {
                    const newIndex = prev < searchResults.length - 1 ? prev + 1 : 0;
                    setTimeout(() => {
                      const dropdown = searchDropdownRef.current;
                      const selectedItem = dropdown?.children[newIndex] as HTMLElement;
                      selectedItem?.scrollIntoView({ block: 'nearest' });
                    }, 0);
                    return newIndex;
                  });
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setSelectedSearchIndex(prev => {
                    const newIndex = prev > 0 ? prev - 1 : searchResults.length - 1;
                    setTimeout(() => {
                      const dropdown = searchDropdownRef.current;
                      const selectedItem = dropdown?.children[newIndex] as HTMLElement;
                      selectedItem?.scrollIntoView({ block: 'nearest' });
                    }, 0);
                    return newIndex;
                  });
                } else if (e.key === 'Enter' && selectedSearchIndex >= 0) {
                  e.preventDefault();
                  const selectedItem = searchResults[selectedSearchIndex];
                  handleItemSelection(selectedItem);
                } else if (e.key === 'Escape') {
                  setSearchResults([]);
                  setSelectedSearchIndex(-1);
                }
              }}
              placeholder={enableItemSearch ? "Search by MFG Part Number, Item Number, Item Description..." : "Search by MFG Part Number..."}
              disabled={editLineData?.isSearchReadOnly}
              style={{
                width: '100%',
                padding: '8px 30px 8px 8px',
                border: `2px solid ${!enableItemSearch && fieldErrors.itemNumber ? '#dc3545' : (isDarkTheme ? '#555' : '#ccc')}`,
                borderRadius: '4px',
                backgroundColor: editLineData?.isSearchReadOnly ? (isDarkTheme ? '#444' : '#f5f5f5') : (isDarkTheme ? '#333' : '#fff'),
                color: editLineData?.isSearchReadOnly ? (isDarkTheme ? '#aaa' : '#666') : (isDarkTheme ? '#fff' : '#000'),
                fontSize: '14px',
                boxSizing: 'border-box' as const,
                height: '40px',
                cursor: editLineData?.isSearchReadOnly ? 'not-allowed' : 'text'
              }}
            />
            {searchTerm && isSearching && (
              <div
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "12px",
                  width: "20px",
                  height: "20px",
                  border: "2px solid #f3f3f3",
                  borderTop: "2px solid #007bff",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite"
                }}
              />
            )}
            {searchTerm && !isSearching && (
              <span
                onClick={() => {
                  setSearchTerm('');
                  setSearchResults([]);
                  setSelectedSearchIndex(-1);
                }}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '12px',
                  cursor: 'pointer',
                  color: isDarkTheme ? '#ccc' : '#666',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  lineHeight: '1',
                  height: '20px',
                }}
              >
                ×
              </span>
            )}
            {!isSearching && !searchTerm && (
              <span
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '10px',
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </span>
            )}
            {searchTerm && searchResults.length > 0 && (
              <div 
                ref={searchDropdownRef}
                style={{
                  position: 'absolute',
                  top: '40px',
                  left: '0',
                  backgroundColor: isDarkTheme ? '#333' : '#fff',
                  border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
                  borderRadius: '4px',
                  zIndex: 1000,
                  width: '100%',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                {searchResults.map((item, index) => (
                  <div
                    key={item.inventoryItemId}
                    onClick={() => handleItemSelection(item)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      backgroundColor: selectedSearchIndex === index ? (isDarkTheme ? '#555' : '#e3f2fd') : 'transparent',
                      color: isDarkTheme ? '#fff' : '#000',
                      borderBottom: `1px solid ${isDarkTheme ? '#555' : '#eee'}`,
                      fontSize: '12px'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedSearchIndex !== index) {
                        e.currentTarget.style.backgroundColor = isDarkTheme ? '#444' : '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedSearchIndex !== index) {
                        e.currentTarget.style.backgroundColor = selectedSearchIndex === index ? (isDarkTheme ? '#555' : '#e3f2fd') : 'transparent';
                      }
                    }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 100px', gap: '8px', alignItems: 'center' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{item.itemNumber}</div>
                      <div style={{ fontSize: '11px', color: isDarkTheme ? '#ccc' : '#666', wordWrap: 'break-word', overflow: 'hidden' }}>{item.description}</div>
                      <div style={{ fontSize: '11px', color: isDarkTheme ? '#aaa' : '#888' }}>{item.mfgPartNumber}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {searchTerm && !isSearching && searchResults.length === 0 && (
              <div style={{
                position: 'absolute',
                top: '40px',
                left: '0',
                backgroundColor: isDarkTheme ? '#333' : '#fff',
                border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
                borderRadius: '4px',
                zIndex: 1000,
                width: '100%',
                padding: '12px',
                color: '#dc3545',
                fontSize: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                boxSizing: 'border-box'
              }}>
                There is no item number associated with this MPN.
              </div>
            )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  id="enableItemSearch"
                  checked={enableItemSearch}
                  onChange={(e) => {
                    setEnableItemSearch(e.target.checked);
                    // Clear itemNumber error when checkbox state changes
                    if (fieldErrors.itemNumber) {
                      setFieldErrors(prev => ({ ...prev, itemNumber: '' }));
                    }
                    // Clear search box when enabling item search
                    if (e.target.checked) {
                      setSearchTerm('');
                      setSearchResults([]);
                      setSelectedSearchIndex(-1);
                    }
                  }}
                  style={{ cursor: 'pointer', width: '20px', height: '20px' }}
                />
                <label htmlFor="enableItemSearch" style={{ cursor: 'pointer', fontSize: '14px', color: isDarkTheme ? '#fff' : '#000' }}>
                  Enable Item Search
                </label>
              </div>
              {!enableItemSearch && fieldErrors.itemNumber && (
                <div style={{
                  color: '#dc3545',
                  fontSize: '12px',
                  marginTop: '5px'
                }}>
                  {fieldErrors.itemNumber}
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '180px', fontSize: '14px', textAlign: 'right', lineHeight: '28px' }}>Serial Number <span style={{ color: 'black' }}>*</span></label>
            <input
              type="text"
              value={formData.serialNumber || ''}
              onChange={(e) => handleInputChange('serialNumber', e.target.value)}
              disabled={true}
              style={{...getInputStyle('serialNumber'), width: '150px', height: '28px', backgroundColor: isDarkTheme ? '#444' : '#f5f5f5', cursor: 'not-allowed'}}
            />
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.serialNumber || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '180px', fontSize: '14px', textAlign: 'right', lineHeight: '28px' }}>Manufacturing Part Number</label>
            <input
              type="text"
              value={formData.assetNumber || ''}
              onChange={(e) => handleInputChange('assetNumber', e.target.value)}
              disabled={true}
              style={{...getInputStyle('assetNumber'), width: '175px', height: '28px', backgroundColor: isDarkTheme ? '#444' : '#f5f5f5', cursor: 'not-allowed'}}
            />
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.assetNumber || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '180px', fontSize: '14px', textAlign: 'right', lineHeight: '28px' }}>Item Number <span style={{ color: 'black' }}>*</span></label>
            <input
              type="text"
              value={formData.itemNumber || ''}
              onChange={(e) => handleInputChange('itemNumber', e.target.value)}
              disabled={true}
              style={{
                ...getInputStyle('itemNumber'), 
                width: '120px', 
                height: '28px', 
                backgroundColor: isDarkTheme ? '#444' : '#f5f5f5', 
                cursor: 'not-allowed', 
                border: enableItemSearch && fieldErrors.itemNumber ? '2px solid #dc3545' : `2px solid ${isDarkTheme ? '#555' : '#ccc'}`
              }}
            />
            <div style={{...errorStyle, marginLeft: '10px'}}>{enableItemSearch && fieldErrors.itemNumber ? fieldErrors.itemNumber : ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '180px', fontSize: '14px', textAlign: 'right', lineHeight: '28px' }}>Item Description</label>
            <input
              type="text"
              value={formData.description || ''}
              onChange={(e) => handleInputChange('description', e.target.value)}
              disabled={true}
              style={{...getInputStyle('description'), width: '400px', height: '28px', backgroundColor: isDarkTheme ? '#444' : '#f5f5f5', cursor: 'not-allowed'}}
            />
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.description || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '180px', fontSize: '14px', textAlign: 'right', lineHeight: '28px' }}>RMA Eligibility <span style={{ color: 'black' }}>*</span></label>
            <div style={{
              width: (formData.repairEligibility === 'Y' || formData.repairEligibility === 'Yes') ? '50px' : 
                     (formData.repairEligibility === 'N' || formData.repairEligibility === 'No') ? '225px' : '50px', 
              height: '28px',
              padding: '6px',
              border: `2px solid ${fieldErrors.repairEligibility ? '#dc3545' : (isDarkTheme ? '#555' : '#ccc')}`,
              borderRadius: '4px',
              backgroundColor: isDarkTheme ? '#444' : '#f5f5f5',
              color: isDarkTheme ? '#aaa' : '#666',
              fontSize: '12px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
              position: 'relative'
            }}>
              {isCheckingEligibility ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid #f3f3f3",
                      borderTop: "2px solid #007bff",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite"
                    }}
                  />
                  <span>Checking...</span>
                </div>
              ) : (
                formData.repairEligibility === 'Y' ? 'Yes' : 
                formData.repairEligibility === 'N' ? 'Serial number not eligible for repair' : 
                formData.repairEligibility === 'Yes' ? 'Yes' :
                formData.repairEligibility === 'No' ? 'Serial number not eligible for repair' :
                formData.repairEligibility || ''
              )}
            </div>
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.repairEligibility || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '180px', fontSize: '14px', textAlign: 'right', lineHeight: '28px' }}>To RAD</label>
            <input
              type="text"
              value={formData.toRad || ''}
              onChange={(e) => handleInputChange('toRad', e.target.value)}
              disabled={true}
              style={{...getInputStyle('toRad'), width: '200px', height: '28px', backgroundColor: isDarkTheme ? '#444' : '#f5f5f5', cursor: 'not-allowed'}}
            />
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.toRad || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '180px', fontSize: '14px', textAlign: 'right', lineHeight: '28px' }}>To Subinventory <span style={{ color: 'black' }}>*</span></label>
            <input
              type="text"
              value={formData.toSubinventory || ''}
              onChange={(e) => handleInputChange('toSubinventory', e.target.value)}
              disabled={true}
              style={{...getInputStyle('toSubinventory'), width: '150px', height: '28px', fontSize: '14px', backgroundColor: isDarkTheme ? '#444' : '#f5f5f5', cursor: 'not-allowed'}}
              placeholder="Enter subinventory"
            />
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.toSubinventory || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '180px', fontSize: '14px', textAlign: 'right', lineHeight: '28px' }}>To Pallet</label>
            <PalletDropdown
              value={formData.toPallet || ''}
              onChange={(value, palletData) => {
                handleInputChange('toPallet', value);
                // Auto-populate subinventory if available
                if (palletData?.subinventoryCode) {
                  handleInputChange('toSubinventory', palletData.subinventoryCode);
                }
              }}
              selectedHeaderToOrg={getToOrgId()}
              isDarkTheme={isDarkTheme}
              width="250px"
              placeholder="Enter pallet"
              toRadValue={toRadFromScanFields}
              orderType={orderType}
              onClear={() => {
                handleInputChange('toPallet', '');
                handleInputChange('toSubinventory', '');
                handleInputChange('toCage', '');
                handleInputChange('toTote', '');
              }}
            />
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.toPallet || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '180px', fontSize: '14px', textAlign: 'right', lineHeight: '28px' }}>To Cage</label>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CageDropdown
                  value={formData.toCage || ''}
                  onChange={(value) => handleInputChange('toCage', value)}
                  selectedHeaderToOrg={getToOrgId()}
                  isDarkTheme={isDarkTheme}
                  width="120px"
                  placeholder="Enter cage"
                  palletValue={formData.toPallet}
                  toRadValue={toRadFromScanFields}
                  onError={(error) => setFieldErrors(prev => ({ ...prev, toCage: error }))}
                />
                <span
                  onClick={() => handleInputChange('toCage', '0')}
                  style={{
                    fontSize: '16px',
                    color: isDarkTheme ? '#007bff' : '#0066cc',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                  }}
                >
                  ⏭
                </span>
              </div>
              {fieldErrors.toCage && (
                <div style={{...errorStyle}}>{fieldErrors.toCage}</div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '180px', fontSize: '14px', textAlign: 'right', lineHeight: '28px' }}>To Tote</label>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ToteDropdown
                  value={formData.toTote || ''}
                  onChange={(value) => handleInputChange('toTote', value)}
                  selectedHeaderToOrg={getToOrgId()}
                  isDarkTheme={isDarkTheme}
                  width="120px"
                  placeholder="Enter tote"
                  palletValue={formData.toPallet}
                  toRadValue={toRadFromScanFields}
                  onError={(error) => setFieldErrors(prev => ({ ...prev, toTote: error }))}
                />
                <span
                  onClick={() => handleInputChange('toTote', '0')}
                  style={{
                    fontSize: '16px',
                    color: isDarkTheme ? '#007bff' : '#0066cc',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                  }}
                >
                  ⏭
                </span>
              </div>
              {fieldErrors.toTote && (
                <div style={{...errorStyle}}>{fieldErrors.toTote}</div>
              )}
            </div>
          </div>
          {editLineData && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ minWidth: '180px', fontSize: '14px', textAlign: 'right', lineHeight: '28px' }}>Tracking Number</label>
              <input
                type="text"
                value={formData.trackingNumber || ''}
                onChange={(e) => handleInputChange('trackingNumber', e.target.value)}
                style={{...getInputStyle('trackingNumber'), width: '200px', height: '28px'}}
                placeholder="Enter tracking number"
              />
              <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.trackingNumber || ''}</div>
            </div>
          )}
        </div>
        {createLocatorError && (
          <div style={{
            marginTop: '15px',
            color: '#dc3545',
            fontSize: '14px',
            padding: '10px',
            backgroundColor: isDarkTheme ? '#2a1f1f' : '#f8d7da',
            border: '1px solid #dc3545',
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            {createLocatorError}
          </div>
        )}
        <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button
            onClick={() => {
              onClose();
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: isDarkTheme ? '#555' : '#ccc',
              color: isDarkTheme ? '#fff' : '#000',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              await handleSave();
              
              // Update status from Error to New when saving via edit mode
              if (editLineData && editLineData.status === 'Error' && onLineUpdate) {
                onLineUpdate(editLineData.id, 'status', 'New');
              }
            }}
            disabled={isSaving}
            style={{
              padding: '8px 16px',
              backgroundColor: isSaving ? '#6c757d' : '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.7 : 1
            }}
          >
            {isSaving ? 'Saving...' : 'Ok'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewLineForm;